import React, { useState } from 'react';
import { Box, Typography, TextField, Button, IconButton, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Save, Send, Upload, Link as LinkIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import VoiceWaveform from './VoiceWaveform.tsx';
import GradientText from '../../../components/common/GradientText.tsx';
import { getAvatarComponent } from '../../../utils/getAvatarComponent.tsx';

const SectionContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  marginBottom: theme.spacing(8),
  position: 'relative',
}));

const SubheadlineText = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: 'rgba(255, 255, 255, 0.8)',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  maxWidth: '800px',
  margin: '0 auto',
}));

const ProblemContainer = styled(Paper)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    background: 'linear-gradient(to bottom, #00CC99, #8A2BE2)',
  },
}));

const ProblemInput = styled(TextField)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    backgroundColor: 'rgba(26, 26, 46, 0.4)',
    borderRadius: theme.spacing(1),
    '& fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#8A2BE2',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#8A2BE2',
  },
}));

interface RecordButtonProps {
  isRecording: boolean;
}

const RecordButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isRecording',
})<RecordButtonProps>(({ theme, isRecording }) => ({
  background: isRecording 
    ? 'linear-gradient(45deg, #FF4500, #FF6347)'
    : 'linear-gradient(45deg, #8A2BE2, #00CC99)',
  color: '#fff',
  padding: theme.spacing(1, 3),
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: isRecording 
    ? '0 4px 20px rgba(255, 69, 0, 0.3)'
    : '0 4px 20px rgba(138, 43, 226, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: isRecording 
      ? 'linear-gradient(45deg, #FF4500, #FF6347)'
      : 'linear-gradient(45deg, #8A2BE2, #00CC99)',
    boxShadow: isRecording 
      ? '0 6px 25px rgba(255, 69, 0, 0.5)'
      : '0 6px 25px rgba(138, 43, 226, 0.5)',
    transform: 'translateY(-2px)',
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: '#fff',
  backgroundColor: 'rgba(26, 26, 46, 0.6)',
  margin: theme.spacing(0, 1),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(138, 43, 226, 0.2)',
    transform: 'translateY(-2px)',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #00CC99, #8A2BE2)',
  color: '#fff',
  padding: theme.spacing(1, 3),
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 4px 20px rgba(0, 204, 153, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #00CC99, #8A2BE2)',
    boxShadow: '0 6px 25px rgba(0, 204, 153, 0.5)',
    transform: 'translateY(-2px)',
  },
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  backgroundColor: 'rgba(26, 26, 46, 0.4)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(0.5),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
}));

interface RecordingIndicatorProps {
  isRecording: boolean;
}

const RecordingIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isRecording',
})<RecordingIndicatorProps>(({ theme, isRecording }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: isRecording ? '#FF4500' : 'rgba(255, 255, 255, 0.3)',
  boxShadow: isRecording ? '0 0 10px #FF4500' : 'none',
  marginRight: theme.spacing(1),
  transition: 'all 0.3s ease',
  animation: isRecording ? 'pulse 1.5s infinite' : 'none',
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(255, 69, 0, 0.7)',
    },
    '70%': {
      boxShadow: '0 0 0 10px rgba(255, 69, 0, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(255, 69, 0, 0)',
    },
  },
}));

const ProblemSourcing: React.FC = () => {
  const [problemDescription, setProblemDescription] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // eslint-disable-next-line no-unused-vars
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const navigate = useNavigate();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleRecordToggle = () => {
    setIsRecording(!isRecording);
  };

  const handleSubmit = () => {
    // Generate a unique ID for the new proposal
    const proposalId = `problem-${Date.now()}`;
    
    // Create a problem proposal template
    const problemProposal = {
      id: proposalId,
      title: problemDescription.length > 50 
        ? `${problemDescription.substring(0, 50)}...` 
        : problemDescription,
      date: new Date().toLocaleDateString(),
      timeline: '3 months',
      timelineType: 'Problem',
      status: 'draft',
      description: problemDescription,
      
      solution: {
        title: 'Proposed Solution',
        description: 'This problem is awaiting solution proposals from our community of experts and AI agents.',
        cost: 'TBD',
        timeframe: 'TBD',
        id: `solution-${Date.now()}`
      },
      
      company: {
        name: 'Your Organization',
        description: 'Information about your organization will be added here.',
        vision: 'Your organization\'s vision will be displayed here.',
        mission: 'Your organization\'s mission will be displayed here.'
      },
      
      objectives: [
        {
          title: 'Define Problem Scope',
          keyResults: [
            { title: 'Identify key stakeholders', progress: 0 },
            { title: 'Document requirements', progress: 0 },
            { title: 'Establish success criteria', progress: 0 }
          ]
        }
      ],
      
      currentProcess: {
        title: 'Current Process',
        description: 'The current process or workflow related to this problem.',
        steps: [
          { number: 1, title: 'Step 1', description: 'Description of current step 1' },
          { number: 2, title: 'Step 2', description: 'Description of current step 2' },
          { number: 3, title: 'Step 3', description: 'Description of current step 3' }
        ]
      },
      
      futureProcess: {
        title: 'Future Process',
        description: 'The improved process after implementing a solution.',
        steps: [
          { number: 1, title: 'Step 1', description: 'Description of future step 1' },
          { number: 2, title: 'Step 2', description: 'Description of future step 2' },
          { number: 3, title: 'Step 3', description: 'Description of future step 3' }
        ]
      },
      
      currentFinancials: [
        { title: 'Current Cost 1', amount: 0, isProfit: false },
        { title: 'Current Cost 2', amount: 0, isProfit: false }
      ],
      
      futureFinancials: [
        { title: 'Future Benefit 1', amount: 0, isProfit: true },
        { title: 'Future Benefit 2', amount: 0, isProfit: true }
      ],
      
      currentUseCases: [
        { title: 'Current Use Case 1', description: 'Description of current use case 1' },
        { title: 'Current Use Case 2', description: 'Description of current use case 2' }
      ],
      
      futureUseCases: [
        { title: 'Future Use Case 1', description: 'Description of future use case 1' },
        { title: 'Future Use Case 2', description: 'Description of future use case 2' }
      ]
    };
    
    // In a real app, you would save the problem proposal to a database
    // For now, we'll just navigate to the proposal detail page
    
    // Store the problem proposal in localStorage so it can be retrieved by the ProposalDetail component
    localStorage.setItem(`proposal-${proposalId}`, JSON.stringify(problemProposal));
    
    // Navigate to the proposal detail page
    navigate(`/proposal-detail/${proposalId}`);
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      
      // Store the file info in localStorage to be accessed later
      const fileInfo = {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified
      };
      
      localStorage.setItem('uploadedFile', JSON.stringify(fileInfo));
      
      // Show success message
      // You can add a snackbar or alert here
      
      // In a real app, you would upload the file to a server
    }
  };

  const handleAddLink = (url: string) => {
    setFileUrl(url);
    
    // Store the URL in localStorage to be accessed later
    localStorage.setItem('linkedUrl', url);
    
    // Show success message
    // You can add a snackbar or alert here
  };

  // Get the VoiceIcon component with appropriate color based on recording state
  const VoiceIcon = getAvatarComponent('VoiceIcon', isRecording ? '#8A2BE2' : '#00CC99', 24);

  return (
    <SectionContainer>
      <GradientText 
        variant="h2" 
        gradient="primary" 
        sx={{ 
          fontSize: '2.5rem', 
          fontWeight: 700, 
          mb: 2, 
          textAlign: 'center' 
        }}
      >
        Problem Sourcing
      </GradientText>
      
      <ProblemContainer>
        <SubheadlineText variant="h6">
          <GradientText variant="body1" gradient="secondary">  
            Turn Any Challenge Into a Solution – Source, Solve, and Evolve
          </GradientText>
        </SubheadlineText>
        
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 4, color: 'rgba(255, 255, 255, 0.7)' }}>
            Present a problem, refine it with collective intelligence, and unlock AI-driven and human-enhanced solutions—all in one seamless flow.
        </Typography>
        <ProblemInput
          label="Describe Your Problem"
          multiline
          rows={4}
          value={problemDescription}
          onChange={(e) => setProblemDescription(e.target.value)}
          variant="outlined"
          placeholder="Describe your business challenge or technical problem in detail..."
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RecordingIndicator isRecording={isRecording} />
            <RecordButton 
              isRecording={isRecording}
              onClick={handleRecordToggle}
              startIcon={VoiceIcon}
            >
              {isRecording ? 'Stop Recording' : 'Start Recording'}
            </RecordButton>
          </Box>
          
          <Box sx={{ flex: 1, mx: 2 }}>
            <VoiceWaveform isActive={isRecording} />
          </Box>
          
          <ActionButtonsContainer>
            <ActionButton 
              disabled={!isRecording && !problemDescription}
              title="Save as draft"
              onClick={() => {
                // Save to localStorage or other storage
                localStorage.setItem('problemDraft', problemDescription);
              }}
            >
              <Save size={20} />
            </ActionButton>
            <ActionButton 
              title="Upload file"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              }}
            >
              <Upload size={20} />
            </ActionButton>
            <ActionButton 
              title="Add link"
              onClick={() => {
                // Show dialog to enter URL
                // Then call handleAddLink with the URL
                const url = prompt('Enter URL to link:');
                if (url) {
                  handleAddLink(url);
                }
              }}
            >
              <LinkIcon size={20} />
            </ActionButton>
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              accept=".csv,.xlsx,.xls,.doc,.docx,.txt" 
              onChange={handleUpload} 
            />
          </ActionButtonsContainer>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <SubmitButton 
            onClick={handleSubmit}
            disabled={!problemDescription.trim()}
            endIcon={<Send size={16} />}
          >
            Problem Submission
          </SubmitButton>
        </Box>
      </ProblemContainer>
    </SectionContainer>
  );
};

export default ProblemSourcing; 